import { computed, inject, Injectable, signal } from "@angular/core";
import { GeofencingDialogResult } from "../../dialog";
import { catchError, EMPTY, finalize, Observable, of, Subscription, switchMap } from "rxjs";
import {
  AreaSelection,
  DetectionObject,
  DetectionResult,
  DetectionResultItem,
  Stage,
} from "../../model/detection/detection.models";
import {
  DEFAULT_IOU_THRESHOLD,
  DEFAULT_THRESHOLD,
  DEFAULT_TILE_BUFFER_PCT,
  DEFAULT_TILE_EDGE_MARGIN_PCT,
  DEFAULT_TILE_ZOOM_OFFSET,
  generateObjectColor,
  MAX_DETECTION_TILES,
  MAX_SEARCH_BUFFER_M,
  TILE_ZOOM,
} from "../../model/detection/detection.tokens";
import { DetectionApiResponse, DetectionCommunicatorService, DetectionRequest, DetectionSourceImage } from "./detection.communicator.service";
import { AreaItem, GeoService } from "../../../viewer-components/services/internal-services/geo.service";
import { MultiPolygon } from "geojson";
import { MapService } from "../../../viewer-components/services/internal-services/map.service";
import { ToastService } from "../../../viewer-components/services/internal-services/toast.service";
import { TOAST_STATUS } from "../../../viewer-components/model/ToastConstants";
import { MaskVectorizerService } from "./mask-vectorizer.service";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";

const initialArea: AreaSelection = { mode: "polygon" };

/** Geographic bbox as [minX, minY, maxX, maxY]. */
type Extent = [number, number, number, number];

/** A polygon set in `polygon-clipping` format: `Array<Polygon>`, each `Polygon` = `[exterior, ...holes]`. */
type PolygonSet = [number, number][][][];

/** Vectorised mask rings as stored on a detection. */
type MaskRings = NonNullable<DetectionResultItem["maskRings"]>;

@Injectable()
export class DetectionService {
  private readonly communicator = inject(DetectionCommunicatorService);

  private readonly mapService = inject(MapService);

  private readonly maskVectorizer = inject(MaskVectorizerService);

  private readonly toast = inject(ToastService);

  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  readonly stage = signal<Stage>("draw");

  readonly area = signal<AreaSelection>(initialArea);

  readonly objects = signal<readonly DetectionObject[]>([]);

  readonly visibility = signal<Readonly<Record<string, boolean>>>({});

  readonly bboxVisibility = signal<Readonly<Record<string, boolean>>>({});

  readonly maskVisibility = signal<Readonly<Record<string, boolean>>>({});

  readonly progress = signal(0);

  /** Buffer in metres applied outward from the detection area. 0 = boundary only. */
  readonly searchBuffer = signal<number>(0);

  /** OL zoom level used when tiling detection areas. */
  readonly tileZoom = signal<number>(TILE_ZOOM);

  /**
   * Server-side overlap buffer added to each tile request so adjacent tiles overlap, as a
   * fraction of a representative tile's average side length. The API only accepts a metres
   * value (`bufferMeter`) — this percentage is converted once per run in {@link buildRequests$}
   * (see {@link tileBufferMetersFromPct}), not sent to the API directly.
   */
  readonly tileBufferPct = signal<number>(DEFAULT_TILE_BUFFER_PCT);

  /**
   * Offset between the user-facing quality zoom ({@link tileZoom}) and the internal OL tile
   * grid zoom used for splitting. Adjustable via the panel's Advanced (Alt+0) section.
   */
  readonly tileZoomOffset = signal<number>(DEFAULT_TILE_ZOOM_OFFSET);

  /** IoU threshold for merging fragmented detections. Pairs with IoU ≥ this value are merged. */
  readonly iouThreshold = signal<number>(DEFAULT_IOU_THRESHOLD);

  /**
   * Inward margin (fraction of tile width/height, 0–0.49) subtracted from each side of a
   * tile's real extent before classifying a detection as crossing that tile's edge — see
   * {@link classifyTileOverlap}. A detection has to stay clear of this border band to be
   * treated as fully contained; anything touching the band is a cross-tile merge candidate,
   * even when it doesn't literally touch the tile boundary line.
   */
  readonly tileEdgeMarginPct = signal<number>(DEFAULT_TILE_EDGE_MARGIN_PCT);

  /**
   * Debug-only flag: when true, merged detections are visually highlighted on the
   * map and on their card so the original fragment bboxes are visible. Toggled
   * via Alt+0 from the panel. Not persisted.
   */
  readonly debugMergedVisible = signal<boolean>(false);

  /**
   * Debug-only flag: when true, the tile grid the current area would be split
   * into (at the selected zoom) is drawn on the map. Toggled from the panel's
   * Advanced (Alt+0) section. Not persisted.
   */
  readonly debugTilesVisible = signal<boolean>(false);

  /**
   * Number of tiles the current area would produce at the selected zoom (for UI display).
   * Matches what {@link GeoService.tileArea} would actually generate, including for
   * non-convex/diagonal areas, while staying bounded in cost — see
   * {@link GeoService.countAreaTiles}.
   */
  readonly tileCount = computed(() => {
    const a = this.area();
    const tilingZoom = this.tileZoom() - this.tileZoomOffset();
    try {
      const tileGrid = this.mapService.getCoreMap()?.getTileGrid();
      if (!tileGrid) return 0;
      const wkts = a.areas?.length ? a.areas.map((ai) => ai.wkt) : GeoService.splitMultiGeometry(a.geometryWkt ?? "");
      return wkts.reduce((sum, wkt) => sum + GeoService.countAreaTiles(wkt, tileGrid, tilingZoom), 0);
    } catch {
      return 0;
    }
  });

  /**
   * WKT of each tile polygon the current area would be split into at the selected zoom
   * (debug visualization). Returns `[]` without tiling when {@link tileCount} is already over
   * {@link MAX_DETECTION_TILES} — the run is blocked past that limit anyway, so there's nothing
   * to gain from building the full grid (with its extra per-tile WKT stringification) just to
   * debug-draw it.
   */
  readonly debugTileWkts = computed<string[]>(() => {
    const a = this.area();
    const tilingZoom = this.tileZoom() - this.tileZoomOffset();
    if (this.tileCount() > MAX_DETECTION_TILES) return [];
    try {
      const tileGrid = this.mapService.getCoreMap()?.getTileGrid();
      if (!tileGrid) return [];
      const wkts = a.areas?.length ? a.areas.map((ai) => ai.wkt) : GeoService.splitMultiGeometry(a.geometryWkt ?? "");
      return wkts.flatMap((wkt) => GeoService.tileArea(wkt, tileGrid, tilingZoom));
    } catch {
      return [];
    }
  });

  /** Set to scroll the detection panel to a specific card. Reset to null after handling. */
  readonly scrollToDetection = signal<{ objectName: string; detectionIndex: number } | null>(null);

  readonly results = signal<Readonly<Record<string, DetectionResult>>>({});

  readonly counts = computed<Record<string, number>>(() => {
    const r = this.results();
    const out: Record<string, number> = {};
    for (const o of this.objects()) out[o.name] = r[o.name]?.detections.length ?? 0;
    return out;
  });

  readonly totalDetections = computed(() => Object.values(this.counts()).reduce((s, n) => s + n, 0));

  readonly visibleDetections = computed(() => {
    const vis = this.visibility();
    const c = this.counts();
    return this.objects()
      .filter((o) => vis[o.name] !== false)
      .reduce((s, o) => s + (c[o.name] ?? 0), 0);
  });

  readonly thresholdRange = computed<[number, number]>(() => {
    const ts = this.objects().map((o) => o.threshold);
    return ts.length ? [Math.min(...ts), Math.max(...ts)] : [DEFAULT_THRESHOLD, DEFAULT_THRESHOLD];
  });

  private readonly _requestCount = signal(0);

  readonly runtimeEstimateSec = computed(() => (this._requestCount() || this.objects().length) * 22);

  private runSubscription: Subscription | null = null;

  /**
   * Incremented each time a run starts or is cancelled. Every async step of a run (mask
   * workers, the post-network finish) captures it and bails out when it no longer matches.
   */
  private runGeneration = 0;

  /**
   * Detection keys already dispatched to {@link MaskVectorizerService} in the current run —
   * guarantees each mask is decoded once, even if the same detection surfaces again through
   * the tile-buffer overlap.
   */
  private readonly queuedMaskKeys = new Set<string>();

  /**
   * Vectorised masks of the current run, keyed by {@link detectionKey}. Kept out of the
   * `results` signal while the run is in progress (nothing renders them during "processing")
   * and applied in a single pass right before the cross-tile merge — see {@link finishRun}.
   */
  private readonly vectorizedMasks = new Map<string, MaskRings>();

  /**
   * In-flight `MaskVectorizerService.vectorize()` calls for the current run. Each mask is
   * dispatched the moment its tile response arrives — `MaskVectorizerService`'s own Web Worker
   * pool is what actually bounds concurrency, so there's no local queue/worker bookkeeping to
   * maintain here. Awaited on network completion before merging fragments.
   */
  private readonly pendingVectorizations = new Set<Promise<void>>();

  /** Masks dispatched so far in the current run — denominator for the 90–97 % progress band. */
  private totalMasksQueued = 0;

  /** Masks settled (success or failure) in the current run — numerator for the 90–97 % progress band. */
  private masksProcessed = 0;

  /** True once every tile request has settled — from then on, progress tracks mask vectorisation. */
  private networkComplete = false;

  /**
   * Original (pre-tiling) area geometry from the last buildRequests() call, for post-run
   * clipping. Combined into a single MultiPolygon once (see {@link GeoService.combinePolygonWkts})
   * so per-detection point-in-polygon checks in {@link applyTileResponse} don't
   * re-parse WKT on every call.
   */
  private _originalAreaGeom: MultiPolygon | null = null;

  /** True when the last buildRequests() applied a server-side bufferMeter to the requests. */
  private _bufferApplied = false;

  /**
   * Geographic bbox of each tile's *buffered* extent (the real tile wkt grown by the
   * `bufferMeter` resolved from {@link tileBufferPct}), same order/index as the requests
   * built in `buildRequests$` —
   * which a detection's `tileIndex` refers back into. Empty when `_bufferApplied`
   * is false (single tile, or no tile buffer — no cross-tile fragments possible).
   * Used by {@link mergeDetectionsForLabel} to restrict the IoU comparison between
   * two detections from different tiles to the zone where both tiles' buffered
   * crops actually overlap, instead of comparing their full (un-clipped) geometries.
   */
  private _tileExtents: (Extent | null)[] = [];

  /**
   * Geographic bbox of each tile's *real* (un-buffered) extent — the actual tile
   * boundary before growing it by the buffer. Same order/index as {@link _tileExtents}.
   * Used by {@link mergeDetectionsForLabel} to tell apart detections that only exist
   * because of the tile buffer (entirely outside their own tile — dropped as duplicates
   * already reported by the tile they truly belong to) from those that genuinely straddle
   * the tile edge (real cross-tile fragment candidates) and those fully inside (complete,
   * never a fragment).
   */
  private _tileExtentsUnbuffered: (Extent | null)[] = [];

  /**
   * Adds a detection object if its name is not already present.
   * Assigns a random vibrant color and default threshold/buffer.
   */
  addObject(spec: Omit<DetectionObject, "color" | "threshold" | "buffer">): void {
    if (this.objects().some((o) => o.name === spec.name)) return;
    this.objects.update((objs) => [
      ...objs,
      {
        ...spec,
        color: generateObjectColor(),
        threshold: DEFAULT_THRESHOLD,
      },
    ]);
  }

  updateObject(name: string, patch: Partial<DetectionObject>): void {
    this.objects.update((objs) => objs.map((o) => (o.name === name ? { ...o, ...patch } : o)));
  }

  /**
   * Renames an existing detection object. No-op if `newName` is empty,
   * identical to the current name, or already taken by another object.
   */
  renameObject(oldName: string, newName: string): void {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName || this.objects().some((o) => o.name === trimmed)) return;
    this.objects.update((objs) => objs.map((o) => (o.name === oldName ? { ...o, name: trimmed } : o)));
  }

  removeObject(name: string): void {
    this.objects.update((objs) => objs.filter((o) => o.name !== name));
    for (const sig of [this.visibility, this.bboxVisibility, this.maskVisibility]) {
      sig.update((v) => {
        const next = { ...v };
        delete next[name];
        return next;
      });
    }
  }

  setStage(stage: Stage): void {
    this.stage.set(stage);
  }

  setArea(area: AreaSelection): void {
    this.area.set(area);
  }

  /** Converts a GeofencingDialogResult into an AreaSelection and applies it. */
  setAreaFromGeofencing(result: GeofencingDialogResult): void {
    if (result.geometryType === "circle") {
      this.setArea({ mode: "point", geometryWkt: result.location, pointRadiusM: result.radiusMeters });
      return;
    }
    const wkt = result.wkt;
    if (wkt?.trim().toUpperCase().startsWith("MULTIPOINT")) {
      // CSV-imported point set → activates per-object buffer slider in configure
      const pointCount = (wkt.match(/-?\d+(?:\.\d+)?\s+-?\d+(?:\.\d+)?/g) ?? []).length;
      this.setArea({
        mode: "locations",
        geometryWkt: wkt,
        importedFile: { name: "imported points", sizeBytes: 0, featureCount: pointCount },
      });
    } else {
      this.setArea({ mode: "boundary", geometryWkt: wkt });
    }
  }

  setVisible(name: string, on: boolean): void {
    this.visibility.update((v) => ({ ...v, [name]: on }));
  }

  setBboxVisible(name: string, on: boolean): void {
    this.bboxVisibility.update((v) => ({ ...v, [name]: on }));
  }

  setMaskVisible(name: string, on: boolean): void {
    this.maskVisibility.update((v) => ({ ...v, [name]: on }));
  }

  setSearchBuffer(metres: number): void {
    this.searchBuffer.set(Math.max(0, Math.min(metres, MAX_SEARCH_BUFFER_M)));
  }

  setTileZoom(zoom: number): void {
    this.tileZoom.set(Math.max(1, Math.round(zoom)));
  }

  setTileBufferPct(value: number): void {
    this.tileBufferPct.set(Math.max(0, value));
  }

  setTileZoomOffset(offset: number): void {
    this.tileZoomOffset.set(Math.max(0, Math.round(offset)));
  }

  setIouThreshold(value: number): void {
    this.iouThreshold.set(Math.max(0, Math.min(1, value)));
  }

  setTileEdgeMarginPct(value: number): void {
    this.tileEdgeMarginPct.set(Math.max(0, Math.min(0.49, value)));
  }

  setScrollToDetection(info: { objectName: string; detectionIndex: number } | null): void {
    this.scrollToDetection.set(info);
  }

  startRun(): void {
    if (this.tileCount() > MAX_DETECTION_TILES) {
      // Defence in depth: the configure panel already disables "Run detection" past this
      // limit, but bail out here too rather than let buildRequests$ tile an area this large —
      // GeoService.tileArea does a real per-tile turf intersection over the full grid, which
      // for an over-limit area can be an unbounded, synchronous main-thread loop.
      this.toast.setNotification({
        id: "detection-tile-limit",
        message: `Area too large for detection: exceeds the ${MAX_DETECTION_TILES}-tile limit.`,
        type: TOAST_STATUS.ERROR,
        timeout: 5000,
        closeButton: true,
      });
      return;
    }
    this.trackObjectTypesSearched();
    this.trackSearched();
    this.runSubscription?.unsubscribe();
    // Invalidate any async work still in flight from a superseded run — unsubscribing
    // stops new emissions but doesn't cancel already-launched decode tasks or a pending finish.
    this.resetRunState();
    const generation = this.runGeneration;
    // Results are accumulated tile by tile from here on (see applyTileResponse).
    this.results.set({});
    this.stage.set("processing");
    this.progress.set(0);

    this.runSubscription = this.buildRequests$()
      .pipe(
        switchMap((requests) =>
          // Reserved to 90% — the remaining 10% covers mask vectorization + cross-tile merge
          // below, which can take a real, visible amount of time for many detections. Without
          // this split, the bar hits 100% the moment tiles finish and then sits there — looking
          // frozen — while that post-processing still runs before the stage actually switches.
          this.communicator.detectAll(requests, (completed, total) => {
            this.progress.set(Math.round((completed / total) * 90));
          }),
        ),
        catchError((err) => {
          console.error("Detection run failed:", err);
          this.stage.set("configure");
          this.toast.setNotification({
            id: "detection-error",
            message: "Detection failed. Please try again.",
            type: TOAST_STATUS.ERROR,
            timeout: 5000,
            closeButton: true,
          });
          return EMPTY;
        }),
        finalize(() => {
          this.runSubscription = null;
        }),
      )
      .subscribe({
        // Each settled tile: append only its own detections and enqueue only its own masks.
        next: ({ index, response }) => this.applyTileResponse(index, response),
        // Only transition the stage once ALL tiles have settled.
        complete: () => {
          void this.finishRun(generation);
        },
      });
  }

  /**
   * Loads a pre-computed detection response into the panel (viewer-handoff plan F3.2): the
   * agent's elements result skips the network phase entirely. Reuses the run machinery —
   * `applyTileResponse` ingestion (mask vectorisation dispatch) and the `finishRun` tail
   * (mask application, cross-tile merge, results/empty stage switch) — but with no tile
   * bookkeeping (`buildRequests$` never ran, so there are no tile extents to filter against
   * and no buffer artifacts) and no GA search events (this is not a user-initiated run).
   */
  loadResults(response: DetectionApiResponse): void {
    const normalized = DetectionService.normalizeSourceImageIds(response);
    this.runSubscription?.unsubscribe();
    this.runSubscription = null;
    this.resetRunState();
    const generation = this.runGeneration;
    // Clean slate for the merge's tile filters — never inherit a previous run's extents.
    this._bufferApplied = false;
    this._originalAreaGeom = null;
    this._tileExtents = [];
    this._tileExtentsUnbuffered = [];
    // One panel object per distinct label, so counts/visibility apply per type.
    const labels = new Set(normalized.features.map((f) => f.properties?.label).filter((l): l is string => !!l));
    for (const label of labels) this.addObject({ name: label, group: "Agent", icon: "category" });
    this.results.set({});
    this.stage.set("processing");
    this.progress.set(0);
    this.applyTileResponse(0, normalized);
    void this.finishRun(generation);
  }

  /** Accepts both `image-id` (viewer contract) and `image_id` (agent wire form) on source images. */
  private static normalizeSourceImageIds(response: DetectionApiResponse): DetectionApiResponse {
    if (!response.images) return response;
    const images = response.images.map((img) => {
      const legacy = (img as { image_id?: unknown })["image_id"];
      if (img["image-id"] || legacy === undefined) return img;
      return { ...img, "image-id": String(legacy) } as DetectionSourceImage;
    });
    return { ...response, images };
  }

  /**
   * Post-network tail of a run: waits for the mask queue to drain (90–97 %), applies the
   * vectorised masks to `results` in one pass, merges cross-tile fragments label by label
   * (97–99 %) and finally switches stage.
   *
   * Yields to the browser between steps so the progress bar actually repaints and the tab
   * stays responsive — every step used to run in one uninterrupted microtask chain, which
   * left the bar frozen at 90 % while the main thread was busy. Aborts silently when the
   * run is cancelled or superseded meanwhile (`runGeneration` changes).
   */
  private async finishRun(generation: number): Promise<void> {
    const isStale = () => this.runGeneration !== generation;
    try {
      this.networkComplete = true;
      this.updateMaskProgress();
      if (this.pendingVectorizations.size) {
        await Promise.allSettled([...this.pendingVectorizations]);
      }
      if (isStale()) return;
      this.progress.set(97);
      await DetectionService.yieldToBrowser();
      if (isStale()) return;

      const withMasks = this.applyVectorizedMasks(this.results());
      const labels = Object.keys(withMasks);
      const merged: Record<string, DetectionResult> = {};
      for (let i = 0; i < labels.length; i++) {
        const label = labels[i];
        merged[label] = { objectName: label, detections: this.mergeDetectionsForLabel(withMasks[label].detections) };
        this.progress.set(97 + Math.round((2 * (i + 1)) / labels.length));
        await DetectionService.yieldToBrowser();
        if (isStale()) return;
      }

      this.results.set(merged);
      this.clearVectorizationState();
      this.progress.set(100);
      setTimeout(() => {
        if (isStale()) return;
        this.stage.set(this.totalDetections() === 0 ? "empty" : "results");
      }, 300);
    } catch (err) {
      console.error("DetectionService: post-processing of detection run failed:", err);
      if (isStale()) return;
      this.clearVectorizationState();
      this.stage.set("configure");
      this.toast.setNotification({
        id: "detection-error",
        message: "Detection failed. Please try again.",
        type: TOAST_STATUS.ERROR,
        timeout: 5000,
        closeButton: true,
      });
    }
  }

  /** Resolves on the next macrotask, letting the browser render and process pending events. */
  private static yieldToBrowser(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 0));
  }

  /** Invalidates the current run's async work and clears all per-run vectorisation state. */
  private resetRunState(): void {
    // eslint-disable-next-line no-plusplus
    this.runGeneration++;
    this.clearVectorizationState();
  }

  /** Drops queued/computed masks and progress counters. Stale in-flight vectorizations exit on their next generation check. */
  private clearVectorizationState(): void {
    this.queuedMaskKeys.clear();
    this.vectorizedMasks.clear();
    this.pendingVectorizations.clear();
    this.totalMasksQueued = 0;
    this.masksProcessed = 0;
    this.networkComplete = false;
  }

  /** Logs one GA event per distinct object type included in the run, so per-type search popularity can be tracked. */
  private trackObjectTypesSearched(): void {
    for (const o of this.objects()) {
      this.googleAnalyticsService.setGoogleAnalyticsEvent(
        GoogleAnalyticsConstantsEvents.CUSTOM_ELEMENT_DETECTION_OBJECT,
        GoogleAnalyticsConstantsCategory.CUSTOM_ELEMENTS_DETECTION,
        true,
        o.name,
      );
    }
  }

  /** Logs GA event for search */
  private trackSearched(): void {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.CUSTOM_ELEMENT_DETECTION_SEARCH,
      GoogleAnalyticsConstantsCategory.CUSTOM_ELEMENTS_DETECTION,
      true,
      this.objects().length > 0
        ? this.objects()
            .map((o) => o.name)
            .join(", ")
        : null,
    );
  }

  /**
   * Cancels the current run. Unsubscribing stops it wherever it currently is — whether
   * still computing buffers in {@link buildRequests$} or already waiting on the API.
   */
  cancelRun(): void {
    this.runSubscription?.unsubscribe();
    this.runSubscription = null;
    // See startRun(): invalidates any vectorization / finish step left in flight by the cancelled run.
    this.resetRunState();
    this.progress.set(0);
    this.stage.set("configure");
  }

  resetForNewRun(): void {
    this.results.set({});
    this.progress.set(0);
    this.stage.set("configure");
  }

  /**
   * Splits the current area geometry into individual sub-geometries, applies the user's
   * search buffer to those (real, un-tiled) sub-geometries, then tiles the buffered result
   * and returns one {@link DetectionRequest} per resulting tile-area. Also updates
   * `_requestCount` so `runtimeEstimateSec` reflects the actual request count.
   *
   * - **MULTIPOLYGON** → one sub-geometry per constituent POLYGON
   * - **MULTIPOINT**   → one sub-geometry per POINT
   * - anything else    → the geometry itself
   *
   * The search buffer is applied to these sub-geometries *before* tiling, not after:
   * buffering post-tiling would expand outward from every artificial tile seam instead of
   * from the true area/point boundary, and would run `priorityClipAreas`'s Voronoi
   * partition across tiles that belong to the same original shape rather than across
   * genuinely distinct areas/points. `_originalAreaGeom` is captured from this buffered
   * result (not the pre-buffer shape), since detections found inside the search-buffer
   * zone are legitimate results the user asked for and must survive the post-run
   * buffer-zone-artifact filter in {@link applyTileResponse}.
   *
   * Once tiled, when multiple polygon requests are produced (tiling or multi-polygon),
   * a server-side `bufferMeter` is applied on top so adjacent tiles overlap and boundary
   * detections merge cleanly — that overlap is trimmed back by the same post-run filter,
   * using `_originalAreaGeom`.
   */
  private buildRequests$(): Observable<DetectionRequest[]> {
    const a = this.area();
    const elements = this.objects().map((o) => ({
      label: o.name,
      detectionThreshold: o.threshold,
    }));
    const searchBuf = this.searchBuffer();
    const tilingZoom = this.tileZoom() - this.tileZoomOffset();
    const tileBufferPct = this.tileBufferPct();
    const tileGrid = this.mapService.getCoreMap().getTileGrid();

    const isPolygonWkt = (w: string) => !w.trimStart().toUpperCase().startsWith("POINT");

    // Real (un-tiled) sub-geometries: pre-split areas from the launcher path, or the
    // current geometry split by type for the draw/import path.
    const subItems: AreaItem[] = a.areas?.length
      ? a.areas
      : GeoService.splitMultiGeometry(a.geometryWkt ?? "").map((wkt) => ({ wkt }));

    const bufferedItems$: Observable<AreaItem[]> =
      searchBuf > 0 ? GeoService.priorityClipAreas(subItems, searchBuf) : of(subItems);

    return bufferedItems$.pipe(
      switchMap((bufferedItems) => {
        // Captured post-buffer: the search-buffer zone is part of the legitimate area now.
        this._originalAreaGeom = GeoService.combinePolygonWkts(
          bufferedItems.map((item) => item.wkt).filter(isPolygonWkt),
        );

        const tiledItems = bufferedItems.flatMap((item) =>
          GeoService.tileArea(item.wkt, tileGrid, tilingZoom).map((tileWkt) => ({ ...item, wkt: tileWkt })),
        );

        let requests: DetectionRequest[] = tiledItems.map((area) => ({
          wkt: area.wkt,
          srid: 4326,
          elements,
          ...(area.collection ? { collection: area.collection } : {}),
        }));

        // The API only accepts a metres buffer — resolve the configured percentage against a
        // single representative tile's average side length once per run (see
        // {@link tileBufferMetersFromPct}), rather than per tile.
        const representativeTileWkt = tiledItems.find((item) => isPolygonWkt(item.wkt))?.wkt;
        const tileBufferM = DetectionService.tileBufferMetersFromPct(representativeTileWkt, tileBufferPct);

        // Apply server-side overlap buffer when multiple polygon requests touch each other.
        const hasMultiplePolygonRequests = requests.length > 1 && requests.some((r) => isPolygonWkt(r.wkt));
        this._bufferApplied = hasMultiplePolygonRequests && tileBufferM > 0;
        if (this._bufferApplied) {
          requests = requests.map((r) => ({ ...r, bufferMeter: tileBufferM }));
        }

        // Same order as `requests`/`tiledItems` — a detection's `tileIndex` looks up here.
        this._tileExtents = this._bufferApplied
          ? tiledItems.map((item) => {
              try {
                return GeoService.wktPolygonToBbox(GeoService.bufferWkt(item.wkt, tileBufferM), false) as Extent;
              } catch (err) {
                console.error("DetectionService: failed to compute buffered tile extent:", err);
                return null;
              }
            })
          : [];

        this._tileExtentsUnbuffered = this._bufferApplied
          ? tiledItems.map((item) => {
              try {
                return GeoService.wktPolygonToBbox(item.wkt, false) as Extent;
              } catch (err) {
                console.error("DetectionService: failed to compute real tile extent:", err);
                return null;
              }
            })
          : [];

        this._requestCount.set(requests.length);
        return of(requests);
      }),
    );
  }

  /**
   * Converts {@link tileBufferPct} into the metres value the API's `bufferMeter` field
   * requires. Resolved once per run against a single representative tile's real ground size
   * (average of its width/height in metres, via the same degrees→metres approximation used
   * elsewhere in this codebase — 111000 m/° latitude, 111000×cos(lat) m/° longitude) rather
   * than per tile, since tiles from the same run sit at nearly the same latitude anyway.
   * Returns 0 when there's no representative tile (e.g. a POINT-only run) or on parse failure.
   */
  private static tileBufferMetersFromPct(representativeTileWkt: string | undefined, pct: number): number {
    if (!representativeTileWkt || pct <= 0) return 0;
    try {
      const [minX, minY, maxX, maxY] = GeoService.wktPolygonToBbox(representativeTileWkt, false) as Extent;
      const midLatRad = ((minY + maxY) / 2) * (Math.PI / 180);
      const widthM = (maxX - minX) * 111000 * Math.cos(midLatRad);
      const heightM = (maxY - minY) * 111000;
      return pct * ((widthM + heightM) / 2);
    } catch (err) {
      console.error("DetectionService: failed to derive tile buffer metres from percentage:", err);
      return 0;
    }
  }

  /**
   * Stable identity for a detection, used to key its vectorised mask while the run is in
   * progress (see {@link vectorizedMasks}). `imageId` (already namespaced per source
   * tile-request) plus the detection's own `bboxWkt` uniquely identifies it regardless of
   * its array position.
   */
  private static detectionKey(det: Pick<DetectionResultItem, "imageId" | "bboxWkt">): string {
    return `${det.imageId}::${det.bboxWkt ?? ""}`;
  }

  /**
   * Recovers the originating tile/request index from a detection's `imageId`.
   * `DetectionCommunicatorService.detectAll` namespaces every `imageId` with a `"{i}:"`
   * prefix (`i` = index into the requests array built by `buildRequests$`, i.e. the tile
   * index) to avoid id collisions across sub-requests — this just reads that same index
   * back. Returns `undefined` when there's no prefix (single-request runs are not
   * namespaced) or the id doesn't match the expected shape.
   */
  private static parseTileIndex(imageId: string): number | undefined {
    const match = /^(\d+):/.exec(imageId);
    return match ? Number(match[1]) : undefined;
  }

  /** True when two extents overlap with positive area (touching edges don't count). */
  private static extentsOverlap(a: Extent, b: Extent): boolean {
    return a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];
  }

  /**
   * Ingests a single settled tile response: converts its detections, appends them to
   * `results` and enqueues their masks for vectorisation. Cost is proportional to *this*
   * tile's detections only — never to everything accumulated so far — so per-tile work
   * stays flat for the whole run and `mergeMap` can keep its request pool saturated.
   *
   * Two cheap filters drop detections that would be discarded later anyway, before any
   * mask is decoded:
   * - centroid outside the original (buffered) area — server-side tile-buffer artifacts;
   * - bbox not touching the detection's own tile's real extent — a duplicate that only
   *   exists because of the tile buffer. The mask is contained in the bbox, so this is the
   *   exact same "outside" verdict {@link classifyTileOverlap} would reach post-run.
   */
  private applyTileResponse(index: number, response: DetectionApiResponse): void {
    const imagesById = new Map<string, (typeof response.images extends Array<infer U> ? U : never) | undefined>();
    for (const img of response.images ?? []) {
      imagesById.set(img["image-id"], img);
    }

    const additions: Record<string, DetectionResultItem[]> = {};
    for (const feature of response.features) {
      const label = feature.properties.label;
      // Feature-level centroid used as fallback when a detection has no bbox.
      const featureCentroid = this.polygonCentroid(feature.geometry.coordinates?.[0] ?? []);
      for (const det of feature.properties.detections) {
        let ext: Extent | null = null;
        if (det.bbox) {
          try {
            ext = (GeoService.wktPolygonToBbox(det.bbox, false) as Extent) ?? null;
          } catch {
            ext = null;
          }
        }
        // Centroid from this detection's own geographic bbox; feature centroid as fallback.
        const detCentroid: [number, number] = ext ? [(ext[0] + ext[2]) / 2, (ext[1] + ext[3]) / 2] : featureCentroid;

        // Skip detections whose centroid falls outside the original area (buffer zone artifacts).
        if (
          this._bufferApplied &&
          this._originalAreaGeom &&
          !GeoService.isPointWithinGeometry(detCentroid, this._originalAreaGeom)
        ) {
          continue;
        }

        const tileIndex = DetectionService.parseTileIndex(det.imageId) ?? (this._bufferApplied ? index : undefined);
        const tileExt = tileIndex !== undefined ? this._tileExtentsUnbuffered[tileIndex] : null;
        if (ext && tileExt && !DetectionService.extentsOverlap(ext, tileExt)) continue;

        const src = imagesById.get(det.imageId);
        const item: DetectionResultItem = {
          imageId: det.imageId,
          confidence: det.confidence ?? 1,
          centroid: detCentroid,
          bboxWkt: det.bbox,
          pixelMask: det["pixel-mask"],
          pixelBbox: det["pixel-bbox"],
          sourceImageLayer: src?.metadata?.layer,
          sourceImageCollection: src?.metadata?.collection,
          captureDate: undefined,
          tileIndex,
        };
        (additions[label] ??= []).push(item);
        if (item.pixelMask) this.enqueueMask(DetectionService.detectionKey(item), item.pixelMask);
      }
    }

    const labels = Object.keys(additions);
    if (labels.length) {
      this.results.update((current) => {
        const next: Record<string, DetectionResult> = { ...current };
        for (const label of labels) {
          next[label] = {
            objectName: label,
            detections: [...(current[label]?.detections ?? []), ...additions[label]],
          };
        }
        return next;
      });
    }
  }

  /**
   * Dispatches a mask to {@link MaskVectorizerService} unless that detection was already
   * enqueued in this run. Fired immediately rather than queued locally — the service's own
   * Web Worker pool is what bounds how many decodes run at once, so there's nothing for
   * `DetectionService` to schedule beyond tracking the in-flight promise for completion/staleness.
   */
  private enqueueMask(key: string, pixelMask: string): void {
    if (this.queuedMaskKeys.has(key)) return;
    this.queuedMaskKeys.add(key);
    this.totalMasksQueued += 1;

    const generation = this.runGeneration;
    const task = this.maskVectorizer
      .vectorize(pixelMask)
      .then((vectorized) => {
        if (this.runGeneration !== generation) return;
        if (vectorized) this.vectorizedMasks.set(key, vectorized);
      })
      .catch((err: unknown) => {
        console.error("DetectionService: mask vectorisation failed:", err);
      })
      .finally(() => {
        if (this.runGeneration === generation) {
          this.masksProcessed += 1;
          this.updateMaskProgress();
        }
        this.pendingVectorizations.delete(task);
      });
    this.pendingVectorizations.add(task);
  }

  /** Once the network phase is over, maps mask-vectorisation progress onto the 90–97 % band. */
  private updateMaskProgress(): void {
    if (!this.networkComplete) return;
    const total = this.totalMasksQueued;
    this.progress.set(total ? 90 + Math.floor((7 * this.masksProcessed) / total) : 97);
  }

  /** Returns `results` with every vectorised mask of the current run applied, in one pass per label. */
  private applyVectorizedMasks(
    current: Readonly<Record<string, DetectionResult>>,
  ): Readonly<Record<string, DetectionResult>> {
    const out: Record<string, DetectionResult> = {};
    for (const [label, group] of Object.entries(current)) {
      out[label] = {
        ...group,
        // Tiles settle in completion order; sort (stable) by tile so the final order is
        // deterministic across runs, matching the request order.
        detections: [...group.detections]
          .sort((a, b) => (a.tileIndex ?? 0) - (b.tileIndex ?? 0))
          .map((det) => {
            const maskRings = this.vectorizedMasks.get(DetectionService.detectionKey(det));
            return maskRings ? { ...det, maskRings } : det;
          }),
      };
    }
    return out;
  }

  /**
   * Returns the centroid (mean of unique vertices) of a polygon's outer ring.
   * Tolerates the trailing closing vertex that GeoJSON requires.
   */
  private polygonCentroid(ring: number[][]): [number, number] {
    if (!ring.length) return [0, 0];
    const first = ring[0];
    const last = ring[ring.length - 1];
    const pts = ring.length > 1 && first[0] === last[0] && first[1] === last[1] ? ring.slice(0, -1) : ring;
    let sx = 0;
    let sy = 0;
    for (const [x, y] of pts) {
      sx += x;
      sy += y;
    }
    return [sx / pts.length, sy / pts.length];
  }

  /**
   * Merges the detections of a single label that were fragmented across tile boundaries:
   * - Drops detections entirely outside their originating tile's real (unbuffered) extent —
   *   buffer-zone-only duplicates already reported by whichever tile they truly belong to.
   * - Only ever compares detections that came from two *different*, *neighbouring* tiles
   *   (buffered extents overlap) — the backend guarantees unique detections within a single
   *   request, so same-tile pairs (and, for single-request runs with no tile info at all,
   *   every pair) are never candidates for merging.
   * - Merges the rest when the IoU of their vectorised mask geometries (reprojected to
   *   geographic space) reaches {@link iouThreshold} — far more precise than comparing
   *   `bboxWkt` rectangles, which overstate overlap for irregular/diagonal shapes. Falls
   *   back to bbox IoU for a pair when either detection has no vectorised mask. Only
   *   detections that straddle their tile's real edge are eligible to merge across tiles.
   *
   * Cost is kept close to linear in the number of detections: `polygon-clipping` (the
   * expensive part) only runs for detections whose bbox can't settle the classification on
   * its own, and for pairs whose clipped bboxes actually overlap inside the shared zone of
   * two neighbouring tiles. Mask polygons are built lazily for just those detections.
   *
   * Merging: geographic bbox is the union of all fragments' bboxes. All other
   * fields (imageId, pixelMask, pixelBbox, maskRings, source metadata) come from
   * the highest-confidence fragment, since pixel-space data is not combinable
   * across different source images.
   */
  private mergeDetectionsForLabel(detections: readonly DetectionResultItem[]): DetectionResultItem[] {
    const allExtents: (Extent | null)[] = detections.map((det) => {
      if (!det.bboxWkt) return null;
      try {
        return GeoService.wktPolygonToBbox(det.bboxWkt, false) as Extent;
      } catch {
        return null;
      }
    });

    const maskPolyCache = new Map<number, PolygonSet | null>();
    const getMaskPoly = (i: number): PolygonSet | null => {
      if (!maskPolyCache.has(i)) maskPolyCache.set(i, this.maskToGeoPolygon(detections[i]));
      return maskPolyCache.get(i) ?? null;
    };

    // Classify each detection against its own tile's real (unbuffered) extent — see
    // {@link classifyTileOverlap}. Detections classified "outside" are dropped: they only
    // exist because of the server-side tile buffer and are already fully captured by
    // whichever tile they truly belong to, so keeping them would surface duplicates.
    const relations = detections.map((det, i) =>
      this.classifyTileOverlap(det.tileIndex, allExtents[i], () => getMaskPoly(i)),
    );

    const keptIdx: number[] = [];
    for (let i = 0; i < detections.length; i++) {
      if (!relations[i].outside) keptIdx.push(i);
    }

    const n = keptIdx.length;
    if (n < 2) return keptIdx.map((i) => detections[i]);

    const threshold = this.iouThreshold();

    // Union-Find with path compression, over positions in `keptIdx`.
    const parent = Array.from({ length: n }, (_, k) => k);
    const find = (idx: number): number => {
      let k = idx;
      while (parent[k] !== k) {
        parent[k] = parent[parent[k]];
        k = parent[k];
      }
      return k;
    };

    // Only detections that reach their own tile's edge band (`crosses`) can be a fragment of
    // an object continuing into a neighbouring tile; bucket them by originating tile so
    // pairs are only ever formed *between* tiles, never within one.
    const crossingByTile = new Map<number, number[]>();
    for (let k = 0; k < n; k++) {
      const i = keptIdx[k];
      const tile = detections[i].tileIndex;
      if (!relations[i].crosses || !allExtents[i] || tile === undefined) continue;
      const bucket = crossingByTile.get(tile);
      if (bucket) bucket.push(k);
      else crossingByTile.set(tile, [k]);
    }

    const tiles = Array.from(crossingByTile.keys());
    for (let a = 0; a < tiles.length; a++) {
      const groupA = crossingByTile.get(tiles[a])!;
      for (let b = a + 1; b < tiles.length; b++) {
        // Only the area within both tiles' buffered crop exists in both detections, so the
        // comparison is restricted to that shared zone — otherwise the object's full extent
        // in one tile dilutes the IoU against the sliver the neighbouring tile could see.
        const zone = this.getTileOverlapZone(tiles[a], tiles[b]);
        if (!zone) continue; // Not neighbours — never merge candidates.
        const groupB = crossingByTile.get(tiles[b])!;

        const zonePoly = DetectionService.extentToPolygonSet(zone);
        const clippedMaskCache = new Map<number, PolygonSet | null>();
        const getClippedMask = (i: number): PolygonSet | null => {
          if (!clippedMaskCache.has(i)) {
            const poly = getMaskPoly(i);
            clippedMaskCache.set(i, poly ? GeoService.intersectPolygonSets(poly, zonePoly) : null);
          }
          return clippedMaskCache.get(i) ?? null;
        };

        for (const kI of groupA) {
          const i = keptIdx[kI];
          const clippedI = DetectionService.clampExtentToZone(allExtents[i]!, zone);
          if (!clippedI) continue;
          for (const kJ of groupB) {
            const j = keptIdx[kJ];
            const clippedJ = DetectionService.clampExtentToZone(allExtents[j]!, zone);
            // Each geometry lies within its bbox: disjoint clipped bboxes ⇒ IoU is 0.
            if (!clippedJ || !DetectionService.extentsOverlap(clippedI, clippedJ)) continue;

            const maskI = getClippedMask(i);
            const maskJ = getClippedMask(j);
            const iou = maskI && maskJ ? this.computeMaskIoU(maskI, maskJ) : this.computeIoU(clippedI, clippedJ);
            if (iou >= threshold) {
              parent[find(kI)] = find(kJ);
            }
          }
        }
      }
    }

    // Group positions by connected component
    const components = new Map<number, number[]>();
    for (let k = 0; k < n; k++) {
      const root = find(k);
      const group = components.get(root);
      if (group) group.push(k);
      else components.set(root, [k]);
    }

    return Array.from(components.values()).map((ks) =>
      ks.length === 1 ? detections[keptIdx[ks[0]]] : this.fuseDetections(ks.map((k) => detections[keptIdx[k]])),
    );
  }

  /**
   * Overlap of two tiles' *buffered* extents, or `null` when they don't overlap — i.e. the
   * tiles aren't neighbours, so no detection pair between them can be a split object.
   */
  private getTileOverlapZone(tileA: number, tileB: number): Extent | null {
    const a = this._tileExtents[tileA];
    const b = this._tileExtents[tileB];
    if (!a || !b || !DetectionService.extentsOverlap(a, b)) return null;
    return DetectionService.intersectExtents(a, b);
  }

  /**
   * Shrinks `ext` inward by `marginPct` of its own width/height on every side — the "inner
   * core" a detection has to stay within to *not* be considered crossing the tile edge. See
   * {@link classifyTileOverlap}.
   */
  private static shrinkExtent(ext: Extent, marginPct: number): Extent {
    const dx = (ext[2] - ext[0]) * marginPct;
    const dy = (ext[3] - ext[1]) * marginPct;
    return [ext[0] + dx, ext[1] + dy, ext[2] - dx, ext[3] - dy];
  }

  /**
   * Classifies a detection against its own tile's real (unbuffered) extent:
   * - `outside`: the detection's geometry never touches the tile's real area — it only
   *   exists because of the server-side tile buffer and is already fully captured (and
   *   reported) by whichever tile it truly belongs to.
   * - `crosses`: the geometry reaches into the {@link tileEdgeMarginPct} inner border band
   *   (not fully contained in the shrunken "inner core") — a genuine candidate for being a
   *   fragment of an object continuing into a neighbouring tile. A detection sitting exactly
   *   on the tile's real edge, but a fraction of the tile size away from a neighbouring
   *   detection reported by the adjacent tile, would otherwise be wrongly classified as fully
   *   contained (`crosses: false`) and never compared for merging — this margin catches that.
   *
   * The bbox settles most cases on its own (the mask always lies within it): bbox disjoint
   * from the tile ⇒ outside; bbox inside the inner core ⇒ contained. Only the remaining
   * ambiguous detections pay for the precise mask polygon (`getMaskPoly`, built lazily).
   *
   * Returns `{ outside: false, crosses: true }` (no restriction) when the detection has no
   * `tileIndex` or its tile's real extent isn't available (e.g. no tile buffering was used).
   */
  private classifyTileOverlap(
    tileIndex: number | undefined,
    ext: Extent | null,
    getMaskPoly: () => PolygonSet | null,
  ): { outside: boolean; crosses: boolean } {
    if (tileIndex === undefined) return { outside: false, crosses: true };
    const tileExt = this._tileExtentsUnbuffered[tileIndex];
    if (!tileExt) return { outside: false, crosses: true };
    const innerExt = DetectionService.shrinkExtent(tileExt, this.tileEdgeMarginPct());

    if (ext) {
      if (!DetectionService.extentsOverlap(ext, tileExt)) return { outside: true, crosses: false };
      const bboxContained =
        ext[0] >= innerExt[0] && ext[1] >= innerExt[1] && ext[2] <= innerExt[2] && ext[3] <= innerExt[3];
      if (bboxContained) return { outside: false, crosses: false };
    }

    const maskPoly = getMaskPoly();
    if (maskPoly) {
      const intersection = GeoService.intersectPolygonSets(maskPoly, DetectionService.extentToPolygonSet(tileExt));
      if (!intersection.length) return { outside: true, crosses: false };
      const innerIntersection = GeoService.intersectPolygonSets(
        maskPoly,
        DetectionService.extentToPolygonSet(innerExt),
      );
      const innerArea = GeoService.polygonSetArea(innerIntersection);
      const geomArea = GeoService.polygonSetArea(maskPoly);
      const contained = geomArea > 0 && innerArea >= geomArea * (1 - 1e-6);
      return { outside: false, crosses: !contained };
    }

    // No mask: bbox overlaps the tile but isn't inside its inner core (or there's no bbox).
    return { outside: false, crosses: true };
  }

  /** Intersection of two extents, as an extent (may be geometrically empty/inverted — see {@link clampExtentToZone}). */
  private static intersectExtents(a: Extent, b: Extent): Extent {
    return [Math.max(a[0], b[0]), Math.max(a[1], b[1]), Math.min(a[2], b[2]), Math.min(a[3], b[3])];
  }

  /** Clips `ext` to `zone`. Returns `null` when the result is empty (they don't overlap). */
  private static clampExtentToZone(ext: Extent, zone: Extent): Extent | null {
    const clipped = DetectionService.intersectExtents(ext, zone);
    return clipped[2] >= clipped[0] && clipped[3] >= clipped[1] ? clipped : null;
  }

  /** Computes Intersection over Union for two axis-aligned bounding boxes in [minX, minY, maxX, maxY] format. */
  private computeIoU(ext1: [number, number, number, number], ext2: [number, number, number, number]): number {
    const ix = Math.max(0, Math.min(ext1[2], ext2[2]) - Math.max(ext1[0], ext2[0]));
    const iy = Math.max(0, Math.min(ext1[3], ext2[3]) - Math.max(ext1[1], ext2[1]));
    const intersection = ix * iy;
    if (intersection === 0) return 0;
    const area1 = (ext1[2] - ext1[0]) * (ext1[3] - ext1[1]);
    const area2 = (ext2[2] - ext2[0]) * (ext2[3] - ext2[1]);
    const union = area1 + area2 - intersection;
    return union > 0 ? intersection / union : 0;
  }

  /** Converts an [minX, minY, maxX, maxY] extent into a single-ring polygon set in `polygon-clipping` format. */
  private static extentToPolygonSet(ext: Extent): PolygonSet {
    const [minX, minY, maxX, maxY] = ext;
    return [
      [
        [
          [minX, minY],
          [maxX, minY],
          [maxX, maxY],
          [minX, maxY],
          [minX, minY],
        ],
      ],
    ];
  }

  /** Computes IoU between two geographic mask polygons — precise shape overlap, not their bboxes. */
  private computeMaskIoU(polyA: PolygonSet, polyB: PolygonSet): number {
    const intersection = GeoService.intersectPolygonSets(polyA, polyB);
    if (!intersection.length) return 0;
    const interArea = GeoService.polygonSetArea(intersection);
    if (interArea <= 0) return 0;
    const areaA = GeoService.polygonSetArea(polyA);
    const areaB = GeoService.polygonSetArea(polyB);
    const union = areaA + areaB - interArea;
    return union > 0 ? interArea / union : 0;
  }

  /**
   * Reprojects a detection's vectorised mask rings (normalised pixel space) into geographic
   * (WGS84) space, using the detection's own `bboxWkt` as the linear frame — the rings are
   * normalised to a canvas aligned with that bbox by the vectoriser, so it's also the correct
   * frame to project back out of. Returns `null` when the detection has no vectorised mask or
   * its bbox can't be parsed.
   */
  private maskToGeoRings(det: DetectionResultItem): Array<[number, number]>[] | null {
    if (!det.maskRings || !det.bboxWkt) return null;
    try {
      const ext = GeoService.wktPolygonToBbox(det.bboxWkt, false) as Extent;
      if (!ext) return null;
      const [minLon, minLat, maxLon, maxLat] = ext;
      const bLon = maxLon - minLon;
      const bLat = maxLat - minLat;
      if (bLon <= 0 || bLat <= 0) return null;
      const { rings, w, h } = det.maskRings;
      if (!w || !h) return null;
      return rings
        .filter((ring) => ring.length >= 3)
        .map((ring) => ring.map((hxy): [number, number] => MaskVectorizerService.projectToGeo(hxy, w, h, ext)));
    } catch (e) {
      console.error("DetectionService: mask-to-geo reprojection failed:", e);
      return null;
    }
  }

  /**
   * Geographic mask polygon for a detection, for use in {@link mergeDetectionsForLabel}'s IoU
   * comparison. Rings are unioned via `polygon-clipping` in case the vectoriser produced
   * multiple/overlapping ring fragments. Returns `null` when the detection has no vectorised
   * mask — callers fall back to the bbox extent.
   */
  private maskToGeoPolygon(det: DetectionResultItem): PolygonSet | null {
    const geoRings = this.maskToGeoRings(det);
    if (!geoRings?.length) return null;
    const union = GeoService.unionPolygons(geoRings);
    return union.length ? union : null;
  }

  /** Merges a group of fragmented detections into one, using the highest-confidence as the source. */
  private fuseDetections(group: DetectionResultItem[]): DetectionResultItem {
    const best = group.reduce((a, b) => (a.confidence >= b.confidence ? a : b));
    const unionExtent = this.computeUnionExtent(group);

    const mergedBboxWkt = unionExtent ? GeoService.bboxToWkt(unionExtent) : best.bboxWkt;
    const mergedCentroid: [number, number] = unionExtent
      ? [(unionExtent[0] + unionExtent[2]) / 2, (unionExtent[1] + unionExtent[3]) / 2]
      : best.centroid;

    const mergedMaskRings = unionExtent ? this.fuseMaskRings(group, unionExtent) : null;
    const mergedFrom = group.map((det) => det.bboxWkt).filter((wkt): wkt is string => !!wkt);

    return {
      ...best,
      bboxWkt: mergedBboxWkt,
      centroid: mergedCentroid,
      maskRings: mergedMaskRings ?? best.maskRings,
      mergedFrom,
    };
  }

  /** Computes the union bbox of all fragments in geographic space (WGS84). */
  private computeUnionExtent(group: DetectionResultItem[]): [number, number, number, number] | null {
    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;

    for (const det of group) {
      if (!det.bboxWkt) continue;
      try {
        const ext = GeoService.wktPolygonToBbox(det.bboxWkt, false) as [number, number, number, number];
        if (ext) {
          if (ext[0] < minLon) minLon = ext[0];
          if (ext[1] < minLat) minLat = ext[1];
          if (ext[2] > maxLon) maxLon = ext[2];
          if (ext[3] > maxLat) maxLat = ext[3];
        }
      } catch (e) {
        console.error("DetectionService: computeUnionExtent failed:", e);
      }
    }
    return isFinite(minLon) ? [minLon, minLat, maxLon, maxLat] : null;
  }

  /**
   * Unions vectorised masks from all fragments and re-projects the result onto a
   * normalised canvas aligned with the fused bbox. Returns `null` when no
   * fragment has vectorised rings (caller falls back to best fragment's mask).
   *
   * Pipeline: each fragment's rings are projected to WGS84 using its own bbox
   * as the linear frame (matching {@link DetectionsLayerService.ringToWkt}),
   * unioned via `polygon-clipping`, then re-projected to `[0..2·NORM]` pixel
   * space so downstream renderers (map layer and card SVG) still work
   * unchanged — they compute `hx/2/w · (extent)` and get the union geometry.
   */
  private fuseMaskRings(
    group: readonly DetectionResultItem[],
    unionExtent: [number, number, number, number],
  ): DetectionResultItem["maskRings"] | null {
    const NORM = 10000;
    const [uMinLon, uMinLat, uMaxLon, uMaxLat] = unionExtent;
    if (uMaxLon - uMinLon <= 0 || uMaxLat - uMinLat <= 0) return null;

    const geoRings = group.flatMap((det) => this.maskToGeoRings(det) ?? []);
    if (!geoRings.length) return null;

    const unionCoords = GeoService.unionPolygons(geoRings);
    if (!unionCoords.length) return null;

    const projectedRings: Array<Array<[number, number]>> = [];
    for (const poly of unionCoords) {
      for (const ring of poly) {
        if (ring.length < 3) continue;
        projectedRings.push(ring.map((lonLat) => MaskVectorizerService.projectToGrid(lonLat, NORM, NORM, unionExtent)));
      }
    }

    return projectedRings.length ? { rings: projectedRings, w: NORM, h: NORM } : null;
  }
}
